/**
 * EventDetailPage - Full event details with distance-aware booking CTAs
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCheckout } from '../../context/CheckoutContext';
import {
  PricingDisplay,
  DualOptionsCard,
  AlternativeStopsDiscovery,
  TimeSlotSelector,
  StartTimePreferenceSelector,
  SimultaneousSessionModal,
} from '../../components/events';
import {
  getEventDetails,
  getEventPricing,
  getEventAvailability,
  getPublicSystemSettings,
  formatDateRange,
  joinWaitlist,
  subscribeInterest,
} from '../../api/events';
import type { TourStop, EventPricingResponse, TimeSlot, TimeSlotSelection, DateViewMode, PublicSystemSettings, DayTimeConfig } from '../../api/events';
import { isMatineeThresholdTriggered, formatTimeDisplay, addMinutes, isValidPostalCode } from '@sda/shared';
import { submitContactForm } from '../../api/inquiries';
import { getFriendlyErrorMessage } from '../../api/client-error';
import { SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL } from '../../lib/contact';
import SmsConsentCheckbox from '../../components/ui/SmsConsentCheckbox';
import { buildLocationParams } from '../../utils/location';

const LEVEL_LABELS: Record<string, string> = {
  type_a: 'Beginner',
  type_b: 'Intermediate (Ages 9-12)',
  type_c: 'Intermediate/Advanced (Ages 12+)',
};

/**
 * Check if class levels are selected for the chosen session(s).
 * Exported for testing.
 */
export function isLevelsComplete(
  timeSlot: TimeSlotSelection | undefined,
  levels: { session1?: string; session2?: string }
): boolean {
  if (!timeSlot) return false;
  if (timeSlot === 'both') return !!levels.session1 && !!levels.session2;
  // Session 2 is specifically '11:00 AM' or 'session_2'; everything else (including Monday slots) maps to session1
  const isSession2 = timeSlot === 'session_2' || timeSlot === '11:00 AM';
  return isSession2 ? !!levels.session2 : !!levels.session1;
}

/**
 * Check if all dates in the availability list are sold out.
 * Exported for testing.
 */
export function isAllDatesSoldOut(availability: Array<{ sold_out: boolean; remaining: number; is_closed?: boolean }>): boolean {
  if (availability.length === 0) return false;
  const nonClosedSlots = availability.filter(s => !s.is_closed);
  if (nonClosedSlots.length === 0) return false;
  return nonClosedSlots.every((s) => s.sold_out || s.remaining <= 0);
}

/**
 * Compute the sidebar "Class Time" label for the current selection.
 * Monday selections are exact multi-slot times, so show the chosen start–end
 * time (e.g. "7:30 PM – 9:00 PM"). Sat/Sun selections keep the fixed-session
 * labels ("Both Class Slots" / "Class Slot 1" / "Class Slot 2").
 * Exported for testing.
 */
export function formatSelectedClassTime(params: {
  timeSlot: TimeSlotSelection | undefined;
  dayType?: 'saturday' | 'sunday' | 'monday';
  startTime?: string;
  endTime?: string;
  sessionDurationMinutes?: number;
}): string {
  const { timeSlot, dayType, startTime, endTime, sessionDurationMinutes } = params;
  if (timeSlot === 'both') return 'Both Class Slots';
  if (dayType === 'monday' && startTime) {
    const resolvedEnd = endTime
      ?? (sessionDurationMinutes != null ? addMinutes(startTime, sessionDurationMinutes) : undefined);
    return `${formatTimeDisplay(startTime)}${resolvedEnd ? ` – ${formatTimeDisplay(resolvedEnd)}` : ''}`;
  }
  return timeSlot === '9:00 AM' ? 'Class Slot 1' : 'Class Slot 2';
}

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, getAccessToken } = useAuth();
  const zip = searchParams.get('zip') || '';
  const lat = searchParams.get('lat') || '';
  const lng = searchParams.get('lng') || '';
  const q = searchParams.get('q') || '';

  // Build a reusable location query string for links
  const locationQuery = buildLocationParams({ zip, lat, lng, q });

  // Determine if we have a valid location (zip or lat/lng)
  const hasLocation = !!zip || (!!lat && !!lng);

  const [event, setEvent] = useState<TourStop | null>(null);
  const [pricing, setPricing] = useState<EventPricingResponse | null>(null);
  const [availability, setAvailability] = useState<TimeSlot[]>([]);
  const [dateViewMode, setDateViewMode] = useState<DateViewMode>('auto');
  const [dayTimeConfig, setDayTimeConfig] = useState<DayTimeConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zipError, setZipError] = useState<string | null>(null);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [showRoleMismatch, setShowRoleMismatch] = useState(false);

  // Selection state
  const [selectedClassId, setSelectedClassId] = useState<string>();
  const [selectedDate, setSelectedDate] = useState<string>();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlotSelection>();
  const [selectedLevels, setSelectedLevels] = useState<{ session1?: string; session2?: string }>({});
  const calendarRef = useRef<HTMLDivElement>(null);
  const sessionPrefRef = useRef<HTMLDivElement>(null);

  // Waitlist state
  const [showWaitlistForm, setShowWaitlistForm] = useState(false);
  const [waitlistName, setWaitlistName] = useState('');
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistError, setWaitlistError] = useState<string | null>(null);

  // Session scheduling state
  const [systemSettings, setSystemSettings] = useState<PublicSystemSettings | null>(null);
  const [startTimePreference, setStartTimePreference] = useState<'early' | 'default' | 'late'>('default');
  const [simultaneousSessions, setSimultaneousSessions] = useState(false);
  const [simultaneousTrigger, setSimultaneousTrigger] = useState<string | null>(null);
  const [showSimultaneousModal, setShowSimultaneousModal] = useState(false);
  const [pendingSimultaneousTrigger, setPendingSimultaneousTrigger] = useState<'matinee_threshold' | 'manual_checkbox' | 'distance_threshold' | null>(null);

  // Get Notified form state
  const [notifyName, setNotifyName] = useState('');
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyError, setNotifyError] = useState<string | null>(null);
  const [notifySuccess, setNotifySuccess] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState('');

  // Load event details and pricing
  useEffect(() => {
    if (!id) return;

    setLoading(true);
    setError(null);
    setZipError(null);

    const location = {
      zip: zip || undefined,
      lat: lat ? parseFloat(lat) : undefined,
      lng: lng ? parseFloat(lng) : undefined,
    };

    Promise.all([
      getEventDetails(id),
      hasLocation
        ? getEventPricing(id, location).catch((err) => {
            // Pricing failed (e.g. invalid ZIP) — capture error but don't block page load
            setZipError(getFriendlyErrorMessage(err, "We couldn't find that location. Please enter a valid US ZIP or Canadian postal code."));
            return null;
          })
        : Promise.resolve(null),
      getEventAvailability(id),
      getPublicSystemSettings().catch(() => null),
    ])
      .then(([eventData, pricingData, availabilityResult, settingsData]) => {
        setEvent(eventData);
        setPricing(pricingData);
        setAvailability(availabilityResult.slots);
        setDateViewMode(availabilityResult.dateViewMode);
        setDayTimeConfig(availabilityResult.dayTimeConfig);
        if (settingsData) setSystemSettings(settingsData);
        // Auto-select if there's only one class option
        if (pricingData?.class_options?.length === 1 && pricingData.class_options[0]) {
          setSelectedClassId(pricingData.class_options[0].id);
        }
      })
      .catch((err) => {
        setError(getFriendlyErrorMessage(err, 'Failed to load event details'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, zip, lat, lng, hasLocation]);

  // Matinee threshold detection
  const isMatineeThresholdActive = (() => {
    if (!systemSettings) return false;
    try {
      return isMatineeThresholdTriggered(
        event?.matinee_time || null,
        systemSettings.matinee_threshold_time,
        systemSettings.default_matinee_time
      );
    } catch {
      return false;
    }
  })();

  const isBothSessions = selectedTimeSlot === 'both';
  // pricing.distance_miles is the user-zip-aware distance; event.straight_line_miles
  // is only populated on the search endpoint, not getEventDetails.
  const isDistanceThresholdTriggered = isBothSessions && (pricing?.distance_miles ?? 0) >= 40;
  const isSimultaneousTriggered = isMatineeThresholdActive || isDistanceThresholdTriggered;

  // Detect if the selected date is sold out
  const isSelectedDateSoldOut = (() => {
    if (!selectedDate) return false;
    const dateSlots = availability.filter((s) => s.date === selectedDate);
    return dateSlots.length > 0 && dateSlots.every((s) => s.sold_out || s.remaining <= 0);
  })();

  // Detect if the selected date is closed (within 21-day booking window)
  const isSelectedDateClosed = (() => {
    if (!selectedDate) return false;
    const dateSlots = availability.filter((s) => s.date === selectedDate);
    return dateSlots.length > 0 && dateSlots.every((s) => s.is_closed === true);
  })();

  // Detect if ALL dates are sold out (no available dates at all)
  const allDatesSoldOut = isAllDatesSoldOut(availability);

  // Pre-fill waitlist email from auth
  useEffect(() => {
    if (user?.email && !waitlistEmail) setWaitlistEmail(user.email);
  }, [user?.email, waitlistEmail]);

  const handleJoinWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setWaitlistSubmitting(true);
    setWaitlistError(null);
    try {
      await joinWaitlist(id, {
        email: waitlistEmail,
        contact_name: waitlistName,
        preferred_date: selectedDate || undefined,
      }, getAccessToken());
      setWaitlistSubmitted(true);
    } catch (err) {
      setWaitlistError(getFriendlyErrorMessage(err, 'Failed to join waitlist'));
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const handleNotifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotifyLoading(true);
    setNotifyError(null);
    try {
      const result = await subscribeInterest({
        email: (isAuthenticated && user?.email) ? user.email : notifyEmail,
        name: notifyName || 'Studio Owner',
        show_id: event?.show.id || '',
      });
      setNotifySuccess(true);
      setNotifyMessage(result.duplicate
        ? "You're already on the list! We'll keep you updated."
        : "You're on the list! We'll email you when new classes are available."
      );
    } catch (err) {
      setNotifyError(getFriendlyErrorMessage(err, 'Failed to subscribe'));
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleTimeSlotSelect = (date: string, timeSlot: TimeSlotSelection | undefined, levels?: { session1?: string; session2?: string }) => {
    setSelectedDate(date);
    setSelectedTimeSlot(timeSlot);
    setSelectedLevels(levels || {});
    // Reset simultaneous state when changing away from 'both'
    if (timeSlot !== 'both') {
      setSimultaneousSessions(false);
      setSimultaneousTrigger(null);
    }
  };

  const effectiveSimultaneous = simultaneousSessions || isSimultaneousTriggered;
  const effectiveTrigger = isMatineeThresholdActive ? 'matinee_threshold' : isDistanceThresholdTriggered ? 'distance_threshold' : simultaneousTrigger;

  // Encode slot in URL — Monday keys include ":" and spaces so they must be URI-encoded
  const bookingUrl = `/booking/${id}?${locationQuery}&class=${selectedClassId}&date=${selectedDate}&slot=${encodeURIComponent(selectedTimeSlot ?? '')}${selectedLevels.session1 ? `&level1=${selectedLevels.session1}` : ''}${selectedLevels.session2 ? `&level2=${selectedLevels.session2}` : ''}${startTimePreference !== 'default' ? `&pref=${startTimePreference}` : ''}${effectiveSimultaneous ? '&simul=1' : ''}${effectiveTrigger ? `&simul_trigger=${effectiveTrigger}` : ''}`;

  // Derive the day_type for the selected date (used to hide preference selector on Monday)
  const selectedDayType = selectedDate
    ? availability.find((s) => s.date === selectedDate)?.day_type
    : undefined;

  // Find the exact availability slot by date + time_slot key. Monday slots need
  // start_time/end_time to render the precise chosen time in the summary (SDA-175).
  const selectedSlotData = (selectedDate && selectedTimeSlot && availability.length > 0)
    ? availability.find((s) => s.date === selectedDate && s.time_slot === selectedTimeSlot)
    : undefined;

  // Validate that levels are selected for the chosen session(s)
  const levelsComplete = isLevelsComplete(selectedTimeSlot, selectedLevels);

  // No auto-scroll after level selection — the integrated card layout
  // keeps everything visible without forced scrolling

  const handleProceedToBooking = () => {
    if (!selectedClassId || !selectedDate || !selectedTimeSlot || !levelsComplete) return;

    if (!isAuthenticated) {
      setShowRoleMismatch(false);
      setShowAuthPrompt(true);
      return;
    }

    // Booking is a studio-only route; surface a clear message instead of letting
    // ProtectedRoute silently redirect non-studio users to the homepage (SDA-147).
    if (user && user.role !== 'studio') {
      setShowAuthPrompt(false);
      setShowRoleMismatch(true);
      return;
    }

    navigate(bookingUrl);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-warm-50">
        <Header />
        <main className="container-wide py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-warm-200 rounded w-1/3" />
            <div className="h-4 bg-warm-200 rounded w-1/4" />
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                <div className="h-64 bg-warm-200 rounded-lg" />
                <div className="h-32 bg-warm-200 rounded-lg" />
              </div>
              <div className="h-96 bg-warm-200 rounded-lg" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error || !event) {
    return (
      <div className="min-h-screen bg-warm-50">
        <Header />
        <main className="container-wide py-16">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-danger-100 flex items-center justify-center mx-auto mb-4">
              <svg
                className="h-8 w-8 text-danger-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-warm-900 mb-2">
              {error || 'Event not found'}
            </h2>
            <p className="text-warm-600 mb-6">
              The event you're looking for may have been removed or is no longer available.
            </p>
            <Link to="/events" className="btn-primary">
              Browse All Events
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Determine which booking UI to show based on pricing tier
  const showDualOptions = pricing?.pricing_tier.tier === 'dual_option';
  const showAlternatives = pricing?.pricing_tier.tier === 'hosted';
  const showDirectBooking =
    pricing &&
    !showDualOptions &&
    !showAlternatives &&
    ['standard', 'tier_1', 'tier_2'].includes(pricing.pricing_tier.tier);

  return (
    <div className="min-h-screen bg-warm-50">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-warm-200">
        <div className="container-wide py-3">
          <nav className="flex items-center gap-2 text-sm text-warm-500">
            <Link to="/" className="hover:text-warm-700">
              Home
            </Link>
            <span>→</span>
            <Link to={`/events?${locationQuery}`} className="hover:text-warm-700">
              Events
            </Link>
            <span>→</span>
            <span className="text-warm-900">{event.show.name}</span>
          </nav>
        </div>
      </div>

      <main className="container-wide py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Event Header */}
            <div className="flex items-start gap-6">
              {event.show.logo_url && (
                <img
                  src={event.show.logo_url}
                  alt={event.show.name}
                  className="w-24 h-24 rounded-lg object-cover shadow-md"
                />
              )}
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-display font-bold text-warm-900 mb-2">
                  Broadway Master Class w/ cast member from {event.show.name}!
                </h1>
                <p className="text-lg text-warm-600">
                  {event.theater.name} · {event.city.name}, {event.city.state}
                </p>
                <p className="text-warm-500 mt-1">
                  {event.show.name} · {formatDateRange(event.start_date, event.end_date)}
                </p>
              </div>
            </div>

            {/* How It Works */}
            <div className="card">
              <h3 className="font-semibold text-warm-900 mb-3">How It Works</h3>
              <p className="text-warm-600 leading-relaxed mb-4">
                A cast member from <span className="font-medium text-warm-800">{event.show.name}</span> will lead a 1.5-hour master class at your studio, offering dancers firsthand insight into the world of a professional dancer.
              </p>
              <p className="text-warm-600 font-medium mb-2">This unique experience includes:</p>
              <ul className="space-y-2 text-warm-600">
                <li className="flex items-start gap-2"><span className="text-success-600">&#10004;&#65039;</span> Choreography in the style of the show (or a requested genre)</li>
                <li className="flex items-start gap-2"><span className="text-success-600">&#10004;&#65039;</span> Q&amp;A + Photo Ops in the final 15 minutes</li>
                <li className="flex items-start gap-2"><span className="text-success-600">&#10004;&#65039;</span> Up to 40 dancers per class</li>
                <li className="flex items-start gap-2"><span className="text-success-600">&#10004;&#65039;</span> Class level recommendations: Beg/Int or Int/Adv (or a single set level)</li>
                <li className="flex items-start gap-2"><span className="text-success-600">&#10004;&#65039;</span> Studio sets the class pricing for its dancers</li>
              </ul>
            </div>

            {/* Invalid ZIP error banner */}
            {zipError && hasLocation && (
              <div className="rounded-lg border-2 border-danger-200 bg-danger-50 p-4" data-testid="zip-error-banner">
                <div className="flex items-start gap-3">
                  <svg className="h-5 w-5 text-danger-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="font-medium text-danger-800">
                      We couldn't find that location. Please enter a valid US ZIP or Canadian postal code.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Distance-Aware Booking UI */}
            {!hasLocation || (zipError && hasLocation) ? (
              <ZipRequiredCard eventId={id!} />
            ) : showAlternatives && pricing ? (
              <div className="card">
                <AlternativeStopsDiscovery
                  pricing={pricing}
                  showId={event.show.id}
                  locationQuery={locationQuery}
                />
              </div>
            ) : showDualOptions && pricing ? (
              <DualOptionsCard
                pricing={pricing}
                tourStopId={id!}
                showId={event.show.id}
                locationQuery={locationQuery}
              />
            ) : showDirectBooking && pricing ? (
              <>
                {/* Pricing */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-warm-900 mb-4">
                    Class Options & Pricing
                  </h3>
                  <PricingDisplay
                    pricing={pricing}
                    selectedClassId={selectedClassId}
                    selectedDate={selectedDate}
                    onSelectClass={(classId) => {
                      setSelectedClassId(classId);
                      // Scroll to calendar after React renders it
                      setTimeout(() => {
                        calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                  />
                </div>

                {/* Availability */}
                {selectedClassId && (
                  <div ref={calendarRef} className="card">
                    <h3 className="text-lg font-semibold text-warm-900 mb-4">
                      Select Date & Time
                    </h3>
                    <TimeSlotSelector
                      availability={availability}
                      selectedDate={selectedDate}
                      selectedTimeSlot={selectedTimeSlot}
                      selectedLevels={selectedLevels}
                      onSelect={handleTimeSlotSelect}
                      dateViewMode={dateViewMode}
                      systemSettings={systemSettings}
                      matineeTime={event?.matinee_time || null}
                      startTimePreference={startTimePreference}
                      forceSimultaneous={simultaneousSessions || isSimultaneousTriggered}
                      isSimultaneousTriggered={isSimultaneousTriggered}
                      sundayMatineeTime={dayTimeConfig?.sunday_matinee_time || '13:00:00'}
                      mondayStartTime={dayTimeConfig?.monday_start_time || '09:00:00'}
                    />
                  </div>
                )}

                {/* All Dates Sold Out — Waitlist (outside selectedClassId guard — uses tour_stop_id) */}
                {allDatesSoldOut && (
                  <div className="card border-2 border-danger-200 bg-danger-50 p-5" data-testid="all-sold-out-banner">
                    {waitlistSubmitted ? (
                      <div className="flex items-start gap-3">
                        <svg className="h-6 w-6 text-success-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <div>
                          <h4 className="font-semibold text-success-900">You've been added to the waitlist!</h4>
                          <p className="text-sm text-success-700 mt-1">
                            We'll notify you at <strong>{waitlistEmail}</strong> if a spot opens up.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start gap-3 mb-4">
                          <svg className="h-6 w-6 text-danger-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <div>
                            <h4 className="font-semibold text-warm-900">All dates are currently sold out</h4>
                            <p className="text-sm text-warm-600 mt-1">
                              All available dates for this event have been filled. Join the waitlist to be notified if a spot opens up.
                            </p>
                          </div>
                        </div>

                        {!showWaitlistForm ? (
                          <button
                            onClick={() => setShowWaitlistForm(true)}
                            className="btn-primary"
                            data-testid="all-sold-out-waitlist-btn"
                          >
                            Join Waitlist
                          </button>
                        ) : (
                          <form onSubmit={handleJoinWaitlist} className="space-y-3" data-testid="all-sold-out-waitlist-form">
                            {waitlistError && (
                              <p className="text-sm text-danger-600">{waitlistError}</p>
                            )}
                            <div>
                              <label className="block text-sm font-medium text-warm-700 mb-1">Name *</label>
                              <input
                                type="text"
                                value={waitlistName}
                                onChange={(e) => setWaitlistName(e.target.value)}
                                placeholder="Your name"
                                className="input w-full"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-warm-700 mb-1">Email *</label>
                              <input
                                type="email"
                                value={waitlistEmail}
                                onChange={(e) => setWaitlistEmail(e.target.value)}
                                placeholder="your@email.com"
                                className="input w-full"
                                required
                              />
                            </div>
                            <div className="flex gap-3">
                              <button
                                type="submit"
                                disabled={waitlistSubmitting}
                                className="btn-primary disabled:opacity-50"
                              >
                                {waitlistSubmitting ? 'Joining...' : 'Join Waitlist'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setShowWaitlistForm(false)}
                                className="btn-secondary"
                              >
                                Cancel
                              </button>
                            </div>
                          </form>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Sold Out — Join Waitlist (per-date) */}
                {selectedClassId && selectedDate && isSelectedDateSoldOut && !isSelectedDateClosed && !allDatesSoldOut && (
                  <div className="card border-danger-200 bg-danger-50/30">
                    {waitlistSubmitted ? (
                      <div className="flex items-start gap-3">
                        <svg className="h-6 w-6 text-success-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <div>
                          <h4 className="font-semibold text-success-900">You've been added to the waitlist!</h4>
                          <p className="text-sm text-success-700 mt-1">
                            We'll notify you at <strong>{waitlistEmail}</strong> if a spot opens up for{' '}
                            {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start gap-3 mb-4">
                          <svg className="h-6 w-6 text-danger-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <div>
                            <h4 className="font-semibold text-warm-900">This date is sold out</h4>
                            <p className="text-sm text-warm-600 mt-1">
                              All faculty spots for{' '}
                              {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}{' '}
                              have been filled. You can join the waitlist to be notified if a spot opens up.
                            </p>
                          </div>
                        </div>

                        {!showWaitlistForm ? (
                          <button
                            onClick={() => setShowWaitlistForm(true)}
                            className="btn-primary"
                          >
                            Join Waitlist
                          </button>
                        ) : (
                          <form onSubmit={handleJoinWaitlist} className="space-y-3">
                            {waitlistError && (
                              <p className="text-sm text-danger-600">{waitlistError}</p>
                            )}
                            <div>
                              <label className="block text-sm font-medium text-warm-700 mb-1">Name *</label>
                              <input
                                type="text"
                                value={waitlistName}
                                onChange={(e) => setWaitlistName(e.target.value)}
                                placeholder="Your name"
                                className="input w-full"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-warm-700 mb-1">Email *</label>
                              <input
                                type="email"
                                value={waitlistEmail}
                                onChange={(e) => setWaitlistEmail(e.target.value)}
                                placeholder="your@email.com"
                                className="input w-full"
                                required
                              />
                            </div>
                            <div className="flex gap-3">
                              <button
                                type="submit"
                                disabled={waitlistSubmitting}
                                className="btn-primary disabled:opacity-50"
                              >
                                {waitlistSubmitting ? 'Joining...' : 'Join Waitlist'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setShowWaitlistForm(false)}
                                className="btn-secondary"
                              >
                                Cancel
                              </button>
                            </div>
                          </form>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Closed Date — Submit Request (per-date) */}
                {selectedClassId && selectedDate && isSelectedDateClosed && (
                  <div className="card border-amber-200 bg-amber-50/30" data-testid="closed-date-waitlist">
                    <div className="flex items-start gap-3 mb-4">
                      <svg className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <h4 className="font-semibold text-warm-900">Direct booking is closed for this date</h4>
                        <p className="text-sm text-warm-600 mt-1">
                          We are too close to the class date for direct booking. Submit an inquiry and we'll check if we can accommodate your booking.
                        </p>
                      </div>
                    </div>

                    <Link
                      to={`/inquiry/${event.id}?${new URLSearchParams({ date: selectedDate!, reason: 'booking_closed' }).toString()}`}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors text-sm"
                      data-testid="closed-date-inquiry-btn"
                    >
                      Inquire About This Date
                    </Link>
                  </div>
                )}

                {/* Manual Simultaneous Checkbox */}
                {selectedTimeSlot === 'both' && !isSimultaneousTriggered && (
                  <div className="card">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={simultaneousSessions}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setPendingSimultaneousTrigger('manual_checkbox');
                            setShowSimultaneousModal(true);
                          } else {
                            setSimultaneousSessions(false);
                            setSimultaneousTrigger(null);
                          }
                        }}
                        className="mt-1 w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                      />
                      <div>
                        <span className="font-medium text-warm-900">I would like both class slots running simultaneously</span>
                        <p className="text-sm text-warm-500 mt-0.5">This requires two separate studios available at the same time.</p>
                      </div>
                    </label>
                  </div>
                )}

                {/* Distance threshold notice — auto-engaged simultaneous mode for 40+ mile venues */}
                {isDistanceThresholdTriggered && (
                  <div className="card border-2 border-amber-200 bg-amber-50">
                    <div className="flex items-start gap-3">
                      <svg className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <div className="font-medium text-warm-900">Both class slots will run simultaneously</div>
                        <p className="text-sm text-warm-700 mt-1">
                          Because this venue is over 40 miles from your studio, both class slots must run at the same time so the cast can make the show. You'll need two separate studios available simultaneously. Our team will confirm this with you after booking.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Start Time Preference — only for Sat/Sun (Monday slot is exact; early/default/late N/A) */}
                {selectedTimeSlot && systemSettings && (selectedDayType === 'saturday' || selectedDayType === 'sunday') && (
                  <div ref={sessionPrefRef}>
                  <StartTimePreferenceSelector
                    systemSettings={systemSettings}
                    matineeTime={event?.matinee_time || null}
                    preference={startTimePreference}
                    onPreferenceChange={setStartTimePreference}
                    isSimultaneous={effectiveSimultaneous}
                    selectedTimeSlot={selectedTimeSlot}
                  />
                  </div>
                )}
              </>
            ) : null}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-3">
              {/* Booking Summary Card */}
              {showDirectBooking && pricing && (
                <div className="card">
                  <h3 className="font-semibold text-warm-900 mb-4">Your Selection</h3>

                  {selectedClassId ? (
                    <div className="space-y-3">
                      {/* Selected Class */}
                      <div className="flex items-center justify-between py-2 border-b border-warm-100">
                        <span className="text-warm-600">Class Type</span>
                        <span className="font-medium text-warm-900">
                          {pricing.class_options.find((c) => c.id === selectedClassId)?.name}
                        </span>
                      </div>

                      {/* Selected Date */}
                      {selectedDate && (
                        <div className="flex items-center justify-between py-2 border-b border-warm-100">
                          <span className="text-warm-600">Class Date:</span>
                          <span className="font-medium text-warm-900">
                            {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                        </div>
                      )}

                      {/* Selected Time */}
                      {selectedTimeSlot && (
                        <div className="flex items-center justify-between py-2 border-b border-warm-100">
                          <span className="text-warm-600">Class Time:</span>
                          <span className="font-medium text-warm-900">
                            {selectedTimeSlot === 'both' ? (
                              <span className="text-primary-600">Both Class Slots</span>
                            ) : (
                              formatSelectedClassTime({
                                timeSlot: selectedTimeSlot,
                                dayType: selectedSlotData?.day_type,
                                startTime: selectedSlotData?.start_time,
                                endTime: selectedSlotData?.end_time,
                                sessionDurationMinutes: systemSettings?.session_duration_minutes,
                              })
                            )}
                          </span>
                        </div>
                      )}

                      {/* Selected Level(s) */}
                      {selectedLevels.session1 && (
                        <div className="py-2 border-b border-warm-100">
                          {selectedTimeSlot === 'both' ? (
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-warm-600 text-sm">Class Slot 1 Level</span>
                                <span className="font-medium text-warm-900 text-sm">{LEVEL_LABELS[selectedLevels.session1] || selectedLevels.session1}</span>
                              </div>
                              {selectedLevels.session2 && (
                                <div className="flex items-center justify-between">
                                  <span className="text-warm-600 text-sm">Class Slot 2 Level</span>
                                  <span className="font-medium text-warm-900 text-sm">{LEVEL_LABELS[selectedLevels.session2] || selectedLevels.session2}</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <span className="text-warm-600">Class Level:</span>
                              <span className="font-medium text-warm-900 text-sm">{LEVEL_LABELS[selectedLevels.session1] || selectedLevels.session1}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Simultaneous Class Slots */}
                      {effectiveSimultaneous && selectedTimeSlot === 'both' && (
                        <div className="flex items-center justify-between py-2 border-b border-warm-100">
                          <span className="text-warm-600">Class Slots</span>
                          <span className="font-medium text-amber-600 text-sm">Simultaneous</span>
                        </div>
                      )}

                      {/* Price */}
                      <div className="flex items-center justify-between py-2">
                        <span className="text-warm-600">Total</span>
                        <div className="text-right">
                          <span className="text-xl font-bold text-warm-900">
                            $
                            {(
                              ((pricing.class_options.find((c) => c.id === selectedClassId)
                                ?.total_price_cents ?? 0) / 100) *
                              (selectedTimeSlot === 'both' ? 2 : 1)
                            ).toFixed(0)}
                          </span>
                          {selectedTimeSlot === 'both' && (
                            <div className="text-xs text-primary-600">2 classes</div>
                          )}
                        </div>
                      </div>

                      {/* Proceed Button */}
                      <button
                        onClick={handleProceedToBooking}
                        disabled={!selectedDate || !selectedTimeSlot || !levelsComplete}
                        className="btn-primary w-full mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {!selectedDate || !selectedTimeSlot
                          ? 'Select Date & Time'
                          : !levelsComplete
                            ? 'Select Class Level'
                            : 'Continue to Booking'}
                      </button>

                      {/* Auth Required Prompt */}
                      {showAuthPrompt && (
                        <div className="mt-4 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                          <div className="flex items-start gap-3">
                            <svg className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <p className="text-sm font-medium text-warm-900 mb-1">
                                Account required to book
                              </p>
                              <p className="text-sm text-warm-600 mb-3">
                                Sign in to your studio account or create one to complete your booking. Your selection will be saved.
                              </p>
                              <div className="flex gap-3">
                                <Link
                                  to="/login"
                                  state={{ from: { pathname: bookingUrl } }}
                                  className="btn-primary text-sm py-1.5 px-4"
                                >
                                  Sign In
                                </Link>
                                <Link
                                  to="/register"
                                  state={{ from: { pathname: bookingUrl } }}
                                  className="btn-outline text-sm py-1.5 px-4"
                                >
                                  Create Account
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Role Mismatch Prompt — non-studio users can't book directly (SDA-147) */}
                      {showRoleMismatch && user && (
                        <div className="mt-4 p-4 bg-warning-50 border border-warning-200 rounded-lg">
                          <div className="flex items-start gap-3">
                            <svg className="h-5 w-5 text-warning-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div>
                              <p className="text-sm font-medium text-warm-900 mb-1">
                                Bookings are made by studio accounts
                              </p>
                              <p className="text-sm text-warm-600 mb-3">
                                {user.role === 'admin' || user.role === 'super_admin'
                                  ? "You're signed in as an admin. To book on behalf of a studio, use the admin Bookings area."
                                  : user.role === 'faculty'
                                    ? "You're signed in as faculty. Only studios can place bookings."
                                    : "Only studio accounts can place bookings."}
                              </p>
                              <div className="flex gap-3">
                                {(user.role === 'admin' || user.role === 'super_admin') && (
                                  <Link
                                    to="/admin/bookings"
                                    className="btn-primary text-sm py-1.5 px-4"
                                  >
                                    Go to Admin Bookings
                                  </Link>
                                )}
                                {user.role === 'faculty' && (
                                  <Link
                                    to="/faculty"
                                    className="btn-primary text-sm py-1.5 px-4"
                                  >
                                    Go to Faculty Dashboard
                                  </Link>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <p className="text-xs text-warm-500 text-center mt-2">
                        {selectedDate && new Date(selectedDate) <= new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)
                          ? 'Full payment required at checkout — class is within 15 days'
                          : <>
                              ${selectedTimeSlot === 'both' ? '200' : '100'} deposit required at checkout
                              {selectedTimeSlot === 'both' && ' ($100 per class)'}
                            </>
                        }
                      </p>
                    </div>
                  ) : (
                    <p className="text-warm-500 text-center py-4">
                      Select a class option to continue
                    </p>
                  )}
                </div>
              )}

              {/* Help / Contact Form */}
              <HelpCard
                event={event}
                pricing={pricing}
                selectedClassId={selectedClassId}
                selectedDate={selectedDate}
                selectedTimeSlot={selectedTimeSlot}
              />

              {/* Get Notified — only when all dates are sold out (per Ryan's SDA-99 follow-up).
                  While a studio can still book this tour stop, asking if they're "interested" is
                  confusing because they're already in the booking flow. */}
              {allDatesSoldOut && (
              <div className="bg-white rounded-xl shadow-sm border border-warm-100 p-6">
                <h3 className="text-lg font-semibold text-warm-900 mb-2">
                  Interested in future master classes with a cast member from this show?
                </h3>
                <p className="text-sm text-warm-500 mb-4">
                  We'll add you to the watchlist for this show and email you when new tour dates are announced.
                </p>

                {notifySuccess ? (
                  <div className="bg-success-50 border border-success-200 rounded-lg p-4 text-center">
                    <svg className="w-8 h-8 text-success-500 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm font-medium text-success-800">{notifyMessage}</p>
                  </div>
                ) : (
                  <form onSubmit={handleNotifySubmit} className="space-y-3">
                    {isAuthenticated && user?.email ? (
                      <p className="text-sm text-warm-600 bg-warm-50 rounded-lg px-3 py-2">
                        Subscribing as <strong>{user.email}</strong>
                      </p>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={notifyName}
                          onChange={(e) => setNotifyName(e.target.value)}
                          placeholder="Your name"
                          required
                          className="w-full px-3 py-2.5 border border-warm-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        <input
                          type="email"
                          value={notifyEmail}
                          onChange={(e) => setNotifyEmail(e.target.value)}
                          placeholder="Your email"
                          required
                          className="w-full px-3 py-2.5 border border-warm-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </>
                    )}
                    {notifyError && (
                      <p className="text-xs text-danger-600">{notifyError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={notifyLoading}
                      className="w-full py-2.5 px-4 min-h-[44px] bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 transition-colors"
                    >
                      {notifyLoading ? 'Submitting...' : 'Notify Me'}
                    </button>
                  </form>
                )}
              </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Simultaneous Sessions Confirmation Modal */}
      {showSimultaneousModal && pendingSimultaneousTrigger && (
        <SimultaneousSessionModal
          trigger={pendingSimultaneousTrigger}
          cityName={event?.city.name}
          onConfirm={() => {
            setSimultaneousSessions(true);
            setSimultaneousTrigger(pendingSimultaneousTrigger);
            setShowSimultaneousModal(false);
            setPendingSimultaneousTrigger(null);
          }}
          onCancel={() => {
            setShowSimultaneousModal(false);
            setPendingSimultaneousTrigger(null);
          }}
        />
      )}

      {/* Footer */}
      <footer className="bg-warm-900 text-warm-300 py-8 mt-16">
        <div className="container-wide">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Stage Door Access" className="h-6 brightness-0 invert" />
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/about" className="hover:text-white transition-colors">
                About
              </Link>
              <a href="mailto:classes@stagedooraccess.com" className="hover:text-white transition-colors">
                Contact
              </a>
              <Link to="/privacy" className="hover:text-white transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-white transition-colors">
                Terms
              </Link>
            </div>
            <p className="text-sm">© {new Date().getFullYear()} Stage Door Access</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * Header component with auth state and cart indicator
 */
function Header() {
  const { isAuthenticated, user, logout, isLoading: authLoading } = useAuth();
  const { getCartSummary } = useCheckout();
  const cartSummary = getCartSummary();

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header className="bg-white border-b border-warm-200 sticky top-0 z-20">
      <div className="container-wide py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Stage Door Access" className="h-7" />
          </Link>
          <div className="flex items-center gap-4">
            {/* Cart Indicator */}
            <Link
              to="/checkout"
              className="relative flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-warm-100 transition-colors group"
            >
              <svg
                className="h-6 w-6 text-warm-600 group-hover:text-warm-900 transition-colors"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              {cartSummary.itemCount > 0 && (
                <>
                  <span className="absolute -top-1 -right-1 flex items-center justify-center h-5 w-5 rounded-full bg-primary-500 text-white text-xs font-bold">
                    {cartSummary.itemCount}
                  </span>
                  <span className="hidden sm:block text-sm font-medium text-warm-700 group-hover:text-warm-900">
                    Cart
                  </span>
                </>
              )}
              {cartSummary.itemCount === 0 && (
                <span className="hidden sm:block text-sm text-warm-500">Cart</span>
              )}
            </Link>

            {authLoading ? (
              <span className="text-warm-400">Loading...</span>
            ) : isAuthenticated ? (
              <>
                <span className="text-warm-500 text-sm hidden sm:block">
                  {user?.email}
                </span>
                <Link
                  to="/dashboard"
                  className="text-warm-600 hover:text-warm-900 transition-colors"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="btn-primary"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-warm-600 hover:text-warm-900 transition-colors">
                  Sign In
                </Link>
                <Link to="/register" className="btn-primary">
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

/**
 * ZIP Required Card - shown when no ZIP is provided
 */
function ZipRequiredCard({ eventId }: { eventId: string }) {
  const [zip, setZip] = useState('');
  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidPostalCode(zip)) {
      navigate(`/events/${eventId}?zip=${encodeURIComponent(zip)}`);
    }
  };

  return (
    <div className="card bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-primary-200 flex items-center justify-center flex-shrink-0">
          <svg
            className="h-6 w-6 text-primary-700"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-warm-900 mb-2">Enter Your Location</h3>
          <p className="text-warm-600 mb-4">
            We need your ZIP or postal code to calculate pricing and display available booking options.
          </p>
          <form onSubmit={handleSubmit} className="flex gap-3">
            <input
              type="text"
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/[^a-zA-Z0-9 ]/g, '').toUpperCase().slice(0, 7))}
              placeholder="ZIP / Postal code"
              className="input w-40"
              maxLength={7}
            />
            <button type="submit" className="btn-primary" disabled={!isValidPostalCode(zip)}>
              View Pricing
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

/**
 * Help / Contact form - captures inquiry with booking context, no auth required
 */
function HelpCard({
  event,
  pricing,
  selectedClassId,
  selectedDate,
  selectedTimeSlot,
}: {
  event: TourStop;
  pricing: EventPricingResponse | null;
  selectedClassId?: string;
  selectedDate?: string;
  selectedTimeSlot?: TimeSlotSelection;
}) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  // Optional SMS/text-message opt-in (A2P/10DLC) — never blocks submit.
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill email if user is authenticated
  const { user } = useAuth();
  useEffect(() => {
    if (user?.email) setEmail((prev) => prev || user.email || '');
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const selectedClassName = pricing?.class_options.find((c) => c.id === selectedClassId)?.name;

    // Determine funnel position
    let funnelPosition = 'browsing';
    if (selectedClassId) funnelPosition = 'class_selected';
    if (selectedDate) funnelPosition = 'date_selected';
    if (selectedTimeSlot) funnelPosition = 'time_selected';

    try {
      await submitContactForm({
        name,
        email,
        phone: phone || undefined,
        message,
        sms_consent: smsConsent,
        context: {
          showId: event.show.id,
          showName: event.show.name,
          tourStopId: event.id,
          venueName: event.theater.name,
          venueCity: event.city.name,
          venueState: event.city.state,
          selectedClass: selectedClassName,
          selectedDate,
          selectedTimeSlot: selectedTimeSlot === 'both' ? 'Both Sessions' : selectedTimeSlot,
          pricingTier: pricing?.pricing_tier.tier,
          distanceMiles: pricing?.distance_miles,
          funnelPosition,
          pageUrl: window.location.href,
        },
      });
      setSubmitted(true);
    } catch (err) {
      setError(getFriendlyErrorMessage(err, 'Failed to submit'));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="card bg-success-50 border-success-200">
        <div className="flex items-center gap-3 mb-2">
          <svg className="h-5 w-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <h4 className="font-semibold text-success-900">Message Sent!</h4>
        </div>
        <p className="text-sm text-success-700">
          We'll get back to you at <strong>{email}</strong> within 48 hours.
        </p>
      </div>
    );
  }

  return (
    <div className="card bg-warm-50 border-warm-200">
      <h4 className="font-semibold text-warm-900 mb-2">Questions?</h4>
      <p className="text-sm text-warm-600 mb-4">
        Have questions about booking? Our team is here to help.
      </p>

      {!showForm ? (
        <div className="space-y-3">
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary w-full text-sm"
          >
            Send Us a Message
          </button>
          <a
            href={`tel:${SUPPORT_PHONE_TEL}`}
            className="flex items-center justify-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            {SUPPORT_PHONE_DISPLAY}
          </a>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          {error && (
            <p className="text-sm text-danger-600">{error}</p>
          )}
          <div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name *"
              className="input w-full text-sm"
              required
            />
          </div>
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address *"
              className="input w-full text-sm"
              required
            />
          </div>
          <div>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Phone (optional)"
              className="input w-full text-sm"
            />
            <SmsConsentCheckbox checked={smsConsent} onChange={setSmsConsent} />
          </div>
          <div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="How can we help? *"
              rows={3}
              className="input w-full text-sm"
              required
              maxLength={2000}
            />
          </div>

          <p className="text-xs text-warm-400">
            Class, venue, and your selections are automatically included.
          </p>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary flex-1 text-sm disabled:opacity-50"
            >
              {submitting ? 'Sending...' : 'Send'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="btn-secondary text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default EventDetailPage;
